import React, { useState, useEffect } from "react";
import { updateAsset } from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { BasePopup } from "components/pop-up/BasePopup";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";

interface AssetGovernancePopupProps {
  isOpen: boolean;
  onClose: () => void;
  assetId: string;
  existingData?: {
    governing_body?: string | null;
    governing_body_contact?: string | null;
    governing_body_region?: string | null;
    governing_body_zone?: string | null;
    governing_body_woreda?: string | null;
    governing_body_house_number?: string | null;
    governing_body_town?: string | null;
    reason_for_governing?: string | null;
    authorizing_body?: string | null;
    governing_duration_start_date?: string | null;
    governing_duration_end_date?: string | null;
    agrement?: string | null;
  };
  onSuccess?: () => void;
  isEditMode?: boolean;
}

export const AssetGovernancePopup: React.FC<AssetGovernancePopupProps> = ({
  isOpen,
  onClose,
  assetId,
  existingData,
  onSuccess,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState({
    governing_body: "",
    governing_body_contact: "",
    governing_body_region: "",
    governing_body_zone: "",
    governing_body_woreda: "",
    governing_body_house_number: "",
    governing_body_town: "",
    reason_for_governing: "",
    authorizing_body: "",
    governing_duration_start_date: "",
    governing_duration_end_date: "",
    agrement: "",
  });

  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Governing body options
  const governingBodyOptions = [
    { value: "Individual", label: "Individual" },
    { value: "Govermental Organization", label: "Governmental Organization" },
    {
      value: "Non Govermental Organization",
      label: "Non Governmental Organization",
    },
  ];

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && existingData) {
        // Edit mode: pre-fill with existing data
        setFormData({
          governing_body: existingData.governing_body || "",
          governing_body_contact: existingData.governing_body_contact || "",
          governing_body_region: existingData.governing_body_region || "",
          governing_body_zone: existingData.governing_body_zone || "",
          governing_body_woreda: existingData.governing_body_woreda || "",
          governing_body_house_number:
            existingData.governing_body_house_number || "",
          governing_body_town: existingData.governing_body_town || "",
          reason_for_governing: existingData.reason_for_governing || "",
          authorizing_body: existingData.authorizing_body || "",
          governing_duration_start_date:
            existingData.governing_duration_start_date?.split("T")[0] || "",
          governing_duration_end_date:
            existingData.governing_duration_end_date?.split("T")[0] || "",
          agrement: existingData.agrement || "",
        });
      } else {
        // Add mode: reset form
        setFormData({
          governing_body: "",
          governing_body_contact: "",
          governing_body_region: "",
          governing_body_zone: "",
          governing_body_woreda: "",
          governing_body_house_number: "",
          governing_body_town: "",
          reason_for_governing: "",
          authorizing_body: "",
          governing_duration_start_date: "",
          governing_duration_end_date: "",
          agrement: "",
        });
        setUploadedFiles([]);
      }
    }
  }, [isOpen, isEditMode, existingData]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.governing_body) return "Governing Body is required.";
    if (!formData.governing_body_contact) return "Governing Name is required.";
    if (!formData.authorizing_body) return "Authorizing Body is required.";
    if (!formData.reason_for_governing)
      return "Reason for Governing is required.";
    if (!formData.governing_duration_start_date)
      return "Governing Start Date is required.";
    if (!formData.governing_duration_end_date)
      return "Governing End Date is required.";

    // Only require agreement file in add mode
    if (!isEditMode && !uploadedFiles) return "Please upload agreement file.";

    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Create FormData for the update
      const submitFormData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (formData[key as keyof typeof formData]) {
          submitFormData.append(key, formData[key as keyof typeof formData]);
        }
      });

      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          submitFormData.append("agrement_documents", file);
        });
      }
      // Call API to update asset governance
      await updateAsset(assetId, submitFormData);

      // Show success message
      showTooltip(
        `Asset governance details ${
          isEditMode ? "updated" : "added"
        } successfully!`,
        "success"
      );

      // Reset form and close popup
      handleClose();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error updating asset governance details:", error);
      const errorMessage =
        error.response?.data?.message ||
        `Failed to ${isEditMode ? "update" : "add"} asset governance details`;
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      governing_body: "",
      governing_body_contact: "",
      governing_body_region: "",
      governing_body_zone: "",
      governing_body_woreda: "",
      governing_body_house_number: "",
      governing_body_town: "",
      reason_for_governing: "",
      authorizing_body: "",
      governing_duration_start_date: "",
      governing_duration_end_date: "",
      agrement: "",
    });
    setUploadedFiles([]);
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Asset Governance" : "Set Asset Governance"}
      description={
        isEditMode
          ? "Update the governance details for this asset."
          : "Set up governance details for this asset."
      }
      w="700px"
    >
      <form onSubmit={handleSubmit}>
        {/* Governing Body */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col mt-4">
            <label className="referral-popup-label">Governing Body</label>
            <select
              className="referral-form-field"
              onChange={handleInputChange}
              name="governing_body"
              value={formData.governing_body}
            >
              <option value="">Select Governing Body</option>
              {governingBodyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Governing Name */}
          <div className="flex flex-col mt-4">
            <label className="referral-popup-label">Governing Name</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="governing_body_contact"
              placeholder="Enter governing name"
              value={formData.governing_body_contact}
            />
          </div>
        </div>

        {/* Governing Location */}
        <div className="mt-4">
          <label className="text-lg font-semibold mb-4 text-[#9D0303]">
            Governing Location
          </label>
          <div className="grid grid-cols-4 gap-3">
            <div className="flex flex-col">
              <label className="referral-popup-label text-xs">Region</label>
              <input
                type="text"
                className="referral-form-field text-sm"
                onChange={handleInputChange}
                name="governing_body_region"
                placeholder="Eg. Region"
                value={formData.governing_body_region}
              />
            </div>

            <div className="flex flex-col">
              <label className="referral-popup-label text-xs">Zone</label>
              <input
                type="text"
                className="referral-form-field text-sm"
                onChange={handleInputChange}
                name="governing_body_zone"
                placeholder="Eg. Zone"
                value={formData.governing_body_zone}
              />
            </div>

            <div className="flex flex-col">
              <label className="referral-popup-label text-xs">Woreda</label>
              <input
                type="text"
                className="referral-form-field text-sm"
                onChange={handleInputChange}
                name="governing_body_woreda"
                placeholder="Eg. Woreda"
                value={formData.governing_body_woreda}
              />
            </div>

            <div className="flex flex-col">
              <label className="referral-popup-label text-xs">Kebele</label>
              <input
                type="text"
                className="referral-form-field text-sm"
                onChange={handleInputChange}
                name="governing_body_town"
                placeholder="Eg. Kebele"
                value={formData.governing_body_town}
              />
            </div>
          </div>
        </div>

        {/* Governing Special Location */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Governing Special Location
          </label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="governing_body_house_number"
            placeholder="Enter special location details"
            value={formData.governing_body_house_number}
          />
        </div>

        {/* Authorizing Body */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Authorizing Body</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="authorizing_body"
            value={formData.authorizing_body}
            placeholder="Authorizing body"
          />
        </div>

        {/* Reason for Governing */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Reason for Governing</label>
          <textarea
            className="referral-form-field h-20"
            onChange={handleInputChange}
            name="reason_for_governing"
            placeholder="Enter reason for governing"
            value={formData.reason_for_governing}
          />
        </div>

        {/* Governing Dates */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Governing Start Date</label>
            <input
              type="date"
              className="referral-form-field"
              onChange={handleInputChange}
              name="governing_duration_start_date"
              value={formData.governing_duration_start_date}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Governing End Date</label>
            <input
              type="date"
              className="referral-form-field"
              onChange={handleInputChange}
              name="governing_duration_end_date"
              value={formData.governing_duration_end_date}
            />
          </div>
        </div>

        {/* Agreement */}

        <MultipleFileUploadComponent
          uploadedFiles={uploadedFiles}
          onFilesChange={setUploadedFiles}
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading
            ? `${isEditMode ? "Updating..." : "Submitting..."}`
            : `${isEditMode ? "Update Governance" : "Add Governance"}`}
        </button>
      </form>
    </BasePopup>
  );
};
